import { Ionicons } from '@expo/vector-icons';
import { CommonActions, RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import * as Linking from 'expo-linking';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Platform, SafeAreaView, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { useAppTheme } from '../../../design-system/ThemeProvider';
import { radius, spacing } from '../../../design-system/tokens';
import { t } from '../../../i18n/translations';
import type { RootStackParamList } from '../../../navigation/types';
import { useAuth } from '../../../shared/hooks/useAuth';
import { supabase } from '../../../shared/services/supabaseService';
import AuthScreen from '../../../shared/ui/AuthScreen';
import { iqesColors } from '../../../shared/ui/iqesDesignSystem';
import PrimaryButton from '../../../shared/ui/PrimaryButton';
import { useLanguageStore } from '../../quiz-core/languageStore';
import { useMultiplayerStore } from '../../quiz-core/multiplayerStore';
import { useUserProfile } from '../../quiz-core/useStore';

type RoomInviteRouteProp = RouteProp<RootStackParamList, 'RoomInviteLanding'>;

const RoomInviteLandingScreen = () => {
  const navigation = useNavigation();
  const route = useRoute<RoomInviteRouteProp>();
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  const { user, loading: authLoading } = useAuth();
  const profile = useUserProfile();
  const { joinRoom } = useMultiplayerStore();
  const currentUrl = Linking.useURL();

  const [showAuth, setShowAuth] = useState(false);
  const [resumeAfterAuth, setResumeAfterAuth] = useState(false);
  const [joining, setJoining] = useState(false);

  const joinCode = useMemo(() => {
    if (typeof route.params?.code === 'string' && route.params.code.trim()) {
      return route.params.code.trim().toUpperCase();
    }

    if (!currentUrl) return '';

    try {
      const parsed = Linking.parse(currentUrl);
      const code = parsed.queryParams?.code;
      return typeof code === 'string' ? code.trim().toUpperCase() : '';
    } catch {
      return '';
    }
  }, [currentUrl, route.params?.code]);

  const roomId = route.params?.roomId;

  const openAuthenticatedRoom = useCallback((resolvedRoomId: string) => {
    navigation.dispatch(
      CommonActions.navigate({
        name: 'AuthenticatedApp',
        params: {
          screen: 'Main',
          params: {
            screen: 'Together',
            params: {
              screen: 'FamilyHub',
              params: {
                screen: 'GameRoom',
                params: {
                  roomId: resolvedRoomId,
                },
              },
            },
          },
        },
      }),
    );
  }, [navigation]);

  const handleJoinRoom = useCallback(async () => {
    if (!roomId || !joinCode) {
      Alert.alert(
        t('familiesFriends.roomInviteInvalidTitle', language),
        t('familiesFriends.roomInviteInvalidBody', language),
      );
      return;
    }

    if (!user) {
      setResumeAfterAuth(true);
      setShowAuth(true);
      return;
    }

    setJoining(true);
    try {
      const { data: existingMembership } = await supabase
        .from('multiplayer_game_room_players')
        .select('room_id')
        .eq('room_id', roomId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingMembership?.room_id) {
        openAuthenticatedRoom(existingMembership.room_id);
        return;
      }

      const result = await joinRoom({
        code: joinCode,
        user_id: profile.id || user.id,
        name: profile.name || 'Player',
        avatar_url: profile.avatar_url || '',
      });

      if (!result.success) {
        if (result.reason === 'room_full') {
          throw new Error(t('multiplayer_lobby.roomFullGeneric', language) || 'This room is full. Please try another.');
        }
        throw new Error(result.message || t('familiesFriends.roomInviteJoinError', language));
      }

      openAuthenticatedRoom(result.room.id);
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : t('familiesFriends.roomInviteJoinError', language);

      Alert.alert(
        t('error', language),
        message,
      );
    } finally {
      setJoining(false);
    }
  }, [joinCode, joinRoom, language, openAuthenticatedRoom, profile.avatar_url, profile.id, profile.name, roomId, user]);

  useEffect(() => {
    if (!resumeAfterAuth || !user || authLoading) {
      return;
    }

    setResumeAfterAuth(false);
    setShowAuth(false);
    handleJoinRoom();
  }, [authLoading, handleJoinRoom, resumeAfterAuth, user]);

  if (showAuth && !user) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
        <AuthScreen onBack={() => setShowAuth(false)} />
      </SafeAreaView>
    );
  }

  const styles = createStyles(colors);

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <View style={styles.heroCard}>
          <View style={styles.badge}>
            <Ionicons name="people" size={20} color={colors.white} />
          </View>
          <Text style={styles.eyebrow}>{t('familiesFriends.roomInviteEyebrow', language)}</Text>
          <Text style={styles.title}>{t('familiesFriends.roomInviteTitle', language)}</Text>
          <Text style={styles.subtitle}>{t('familiesFriends.roomInviteSubtitle', language)}</Text>

          <View style={styles.codeCard}>
            <Text style={styles.codeLabel}>{t('familiesFriends.roomInviteCodeLabel', language)}</Text>
            <Text style={styles.codeValue}>{joinCode || '----'}</Text>
          </View>

          <Text style={styles.supportCopy}>
            {user
              ? t('familiesFriends.roomInviteSignedInHint', language)
              : t('familiesFriends.roomInviteAuthHint', language)}
          </Text>

          <PrimaryButton
            title={joining ? t('familiesFriends.roomInviteJoining', language) : t('familiesFriends.roomInviteContinue', language)}
            onPress={handleJoinRoom}
            disabled={joining || !joinCode}
            style={styles.primaryButton}
          />

          {!user && (
            <TouchableOpacity
              onPress={() => {
                setResumeAfterAuth(false);
                setShowAuth(true);
              }}
              style={styles.secondaryButton}
            >
              <Text style={styles.secondaryButtonText}>{t('familiesFriends.roomInviteSignIn', language)}</Text>
            </TouchableOpacity>
          )}

          {Platform.OS === 'web' && (
            <Text style={styles.webHint}>{t('familiesFriends.roomInviteWebHint', language)}</Text>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const createStyles = (_colors: ReturnType<typeof useAppTheme>['colors']) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: iqesColors.canvas,
  },
  contentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  heroCard: {
    backgroundColor: 'rgba(8,10,24,0.94)',
    borderRadius: radius['2xl'],
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.38)',
    padding: spacing.xl,
    alignItems: 'center',
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 10,
  },
  badge: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: 'rgba(246,190,58,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.42)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  eyebrow: {
    color: iqesColors.goldSoft,
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: spacing.sm,
  },
  title: {
    color: iqesColors.ivory,
    fontSize: 28,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    color: 'rgba(255,248,232,0.74)',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.lg,
    maxWidth: 420,
  },
  codeCard: {
    width: '100%',
    backgroundColor: 'rgba(246,190,58,0.10)',
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: 'rgba(246,190,58,0.36)',
    padding: spacing.lg,
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  codeLabel: {
    color: 'rgba(255,248,232,0.66)',
    fontSize: 13,
    fontWeight: '800',
    marginBottom: spacing.xs,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  codeValue: {
    color: iqesColors.goldBright,
    fontSize: 26,
    fontWeight: '900',
    letterSpacing: 2,
  },
  supportCopy: {
    color: 'rgba(255,248,232,0.72)',
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  primaryButton: {
    alignSelf: 'stretch',
    marginBottom: spacing.md,
  },
  secondaryButton: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  secondaryButtonText: {
    color: iqesColors.goldSoft,
    fontSize: 15,
    fontWeight: '900',
  },
  webHint: {
    color: 'rgba(255,248,232,0.62)',
    fontSize: 13,
    textAlign: 'center',
    marginTop: spacing.sm,
  },
});

export default RoomInviteLandingScreen;
